// src/data/claimOutcome.js
//
// HOW A CLAIM ENDED — the outcome vocabulary, the reason gate, and the roll-ups (batch 5).
//
// WHY THIS FILE IS THE ONE THAT MATTERS. Until it exists the platform has never recorded how a
// claim ended. Not "recorded it badly" - never recorded it at all. That is ISS-36, and it is why
// nothing in this system can learn: a committee that does not know which claims it won cannot be
// told anything by its own history. Every roll-up, every "similar cases up/down", every question
// about whether the platform is working, waits on this field.
//
// -------------------------------------------------------------------------------------------
// THE RULINGS, NAMED SO A LATER READER CAN CHECK RATHER THAN TRUST
//
//   68.1   THE CLOSED SET OF EIGHT. Adopted as proposed. The keys are at 61.9.
//   61.9   The eight values with their labels, plus `outcomeReason` and `amountRecovered`.
//   61.9a  THE REASON IS REQUIRED ON ALL EIGHT. Bob overruled the assistant's narrower scoping,
//          which would have required it only on the four adverse outcomes. His reason is the
//          stronger one and it is recorded here because it shapes the whole module:
//          THE REASON ON A WIN IS WHAT TELLS THE NEXT OFFICER WHAT WORKED. A committee that
//          records why it lost and not why it won learns half of what its own record holds.
//   68.2   NOTHING IS BACK-FILLED BY INFERENCE (PD-4). A closed claim with no recorded outcome
//          reads as UNRECORDED - not as any of the eight, and never as a denial. The history
//          starts the day the field does. Twelve claims exist today and none carries an outcome.
//   68.2   `progressed_to_board` is the JOIN to data\YCP_ARBITRATION_DOCKET.csv and should carry
//          the Board case number, or the two records drift the way the four Fourth Division cases
//          sat unlinked to anything for two months.
// -------------------------------------------------------------------------------------------

import { LADDER } from './claimLadder';
import { resolveGeneralCommittee } from './routing';   // A12 / SS XI: one definition of the rule
import { lastDayOf } from './timeLimit';   // ruling 314 (forensic audit 2026-10-16)

// THE CLOSED SET. Eight values, keys as ruling 61.9 has them. Nothing is added to this list without
// a ruling: a ninth value invented in a UI is a vocabulary change made by whoever was typing.
export const OUTCOMES = [
  { key: 'allowed_full',       label: 'Allowed in full',      adverse: false, win: true },
  { key: 'allowed_part',       label: 'Allowed in part',      adverse: false, win: true },
  { key: 'settled',            label: 'Settled',              adverse: false, win: true },
  { key: 'withdrawn',          label: 'Withdrawn',            adverse: true,  win: false },
  { key: 'denied_merits',      label: 'Denied on the merits', adverse: true,  win: false },
  { key: 'denied_procedure',   label: 'Denied on procedure',  adverse: true,  win: false },
  { key: 'progressed_to_board', label: 'Progressed to a Board', adverse: false, win: null },
  { key: 'abandoned',          label: 'Abandoned',            adverse: true,  win: false },
];

export const OUTCOME_KEYS = OUTCOMES.map(o => o.key);

// `progressed_to_board` is neither won nor lost - the matter simply left this platform for a Board,
// and forcing it into a win column would put a number in a roll-up that means nothing. It carries
// win: null and is counted in its own bucket.
export function outcomeMeta(key) {
  return OUTCOMES.find(o => o.key === String(key || '')) || null;
}

export function outcomeLabel(key) {
  const m = outcomeMeta(key);
  return m ? m.label : '';
}

// THE STAGE AT WHICH AN OUTCOME IS RECORDED. A claim is closed at `closure`; an outcome set earlier
// would be a prediction, and this platform does not predict (ruling 27).
export const CLOSING_STAGE = 'closure';

export function isClosingStage(status) {
  return String(status || '') === CLOSING_STAGE;
}

// ---------------------------------------------------------------------------------------------
// THE GATE. 61.9a: no claim may be closed without a reason, on any of the eight.
//
// Returns { ok, errors[], patch } - never a partial patch. `patch` is null unless every check
// passes, because a half-applied closure is worse than one that did not apply.
//
// THE KNOWN RISK IS NOT RE-ARGUED HERE, it is ANSWERED, exactly as 61.9a says: a required field can
// become a place people type nonsense. The answers are (a) the field is short, and (b) IT IS NEVER
// PRE-FILLED - so a blank is visibly a blank rather than a default nobody read. There is deliberately
// no default value anywhere in this module.
// ---------------------------------------------------------------------------------------------

// RULING 161 (2026-09-17): a recovered figure says what it rests on. The first three are SOURCED - a
// document or a pay record an officer can point to. 'officer_statement' is what he remembers or was
// told; it is kept, shown, and NEVER added to a sourced total. A figure typed with no basis at all is
// 'unsourced' and is treated the same way. Claim-VALUE placeholders (ruling 93, claimValue.js) are a
// different thing entirely - what a claim might be worth - and no roll-up here ever reads them.
export const AMOUNT_BASES = [
  { key: 'award_document',      label: 'Award or decision document', sourced: true },
  { key: 'settlement_document', label: 'Settlement document',        sourced: true },
  { key: 'pay_record',          label: 'Pay record / pay stub',      sourced: true },
  { key: 'officer_statement',   label: 'Officer\'s statement (no document)', sourced: false },
  { key: 'unsourced',           label: 'No basis recorded',          sourced: false },
];
export const AMOUNT_BASIS_KEYS = AMOUNT_BASES.map(b => b.key);
export function amountBasisMeta(key) { return AMOUNT_BASES.find(b => b.key === String(key || '')) || null; }
export function isSourcedAmount(claim) {
  const m = amountBasisMeta(claim && claim.amountBasis);
  return !!(m && m.sourced) && typeof (claim && claim.amountRecovered) === 'number' && isFinite(claim.amountRecovered);
}

export const REASON_MIN = 4;      // shorter than a word is not a reason; it is a keystroke
export const REASON_MAX = 400;    // "keep the field short" (61.9a)

export function validateClosure(input) {
  const i = input || {};
  const errors = [];

  const key = String(i.outcome || '').trim();
  if (!key) {
    errors.push('An outcome is required. There is no default and nothing is inferred: a claim closed '
              + 'without one reads as UNRECORDED forever (68.2).');
  } else if (OUTCOME_KEYS.indexOf(key) === -1) {
    errors.push('"' + key + '" is not one of the eight. The set is closed (68.1) and is not extended '
              + 'from a screen.');
  }

  // 61.9a - all eight, including the wins.
  const reason = String(i.outcomeReason == null ? '' : i.outcomeReason).trim();
  if (!reason) {
    errors.push('A reason is required on ALL EIGHT outcomes, wins included (61.9a). The reason on a '
              + 'win is what tells the next officer what worked.');
  } else if (reason.length < REASON_MIN) {
    errors.push('That is too short to be a reason (' + reason.length + ' characters). Say what '
              + 'actually decided it.');
  } else if (reason.length > REASON_MAX) {
    errors.push('Keep it short - ' + REASON_MAX + ' characters at most (61.9a). Detail belongs in a note.');
  }

  // amountRecovered. OPTIONAL and NULLABLE ON PURPOSE, and the distinction is the point:
  //   null   nobody recorded a figure. UNKNOWN.
  //   0      recorded, and the answer was nothing.
  // A roll-up that reads an unknown as zero reports a committee recovering nothing when in truth
  // nobody entered a number, and that is the PD-4 failure this whole platform is built against.
  let amount = null;
  if (i.amountRecovered !== undefined && i.amountRecovered !== null && String(i.amountRecovered).trim() !== '') {
    const n = Number(i.amountRecovered);
    if (!isFinite(n) || n < 0) {
      errors.push('Amount recovered must be a number of dollars, or left blank. Blank means nobody '
                + 'recorded a figure; zero means the claim recovered nothing. They are different facts.');
    } else {
      amount = n;
    }
  }

  // 68.2 - `progressed_to_board` is the join to the arbitration docket. Not a hard requirement,
  // because the Board case number often is not known on the day it is sent, but it is WARNED about
  // rather than passed over: the four Fourth Division cases sat unlinked to anything for two months.
  const warnings = [];
  const boardCase = String(i.boardCaseNumber || '').trim();
  if (key === 'progressed_to_board' && !boardCase) {
    warnings.push('No Board case number recorded. This outcome is the one join between a claim and '
                + 'data\\YCP_ARBITRATION_DOCKET.csv (68.2); without it the two records drift. Add it '
                + 'when the Board assigns it.');
  }

  if (errors.length) return { ok: false, errors, warnings, patch: null };

  // ruling 161: the basis travels with the figure. Unknown basis -> 'unsourced', never a guess; a basis
  // outside the list is an error, not a new word; no figure -> no basis.
  let basis = null;
  if (amount !== null) {
    const b = String(i.amountBasis || '').trim();
    if (b && !AMOUNT_BASIS_KEYS.includes(b)) { errors.push('The basis of the amount must be one of: ' + AMOUNT_BASIS_KEYS.join(', ') + '.'); return { ok: false, errors, warnings, patch: null }; }
    basis = b || 'unsourced';
    if (!amountBasisMeta(basis).sourced) warnings.push('The recovered figure has no document behind it (' + amountBasisMeta(basis).label + '). It is kept and shown, and it is never added to the committee\'s sourced total. [R-161]');
  }
  const patch = {
    outcome: key,
    outcomeReason: reason,
    amountRecovered: amount,          // null is a real value here and means "not recorded"
    amountBasis: basis,               // ruling 161: null when there is no figure
  };
  if (boardCase) patch.boardCaseNumber = boardCase;
  return { ok: true, errors: [], warnings, patch };
}

// Has this claim had its ending recorded at all? 68.2: absence is UNRECORDED, never a denial.
export function hasOutcome(claim) {
  return !!claim && OUTCOME_KEYS.indexOf(String(claim.outcome || '')) !== -1;
}

// ---------------------------------------------------------------------------------------------
// THE ROLL-UPS. 68.2: every aggregation is already a projection of `scopeKey`, so once outcomes
// exist the counting is nearly free.
//
//   individual  createdBy
//   local       the LC<digits> segment of scopeKey
//   gca         the -<digits> segment of scopeKey
//   committee   the claim's committee field
//
// EVERY BUCKET CARRIES `unrecorded` AND IT IS NEVER FOLDED INTO A DENIAL OR DROPPED FROM THE TOTAL.
// A committee looking at "8 of 12 recorded" is being told the truth; one looking at a clean pie
// chart over 8 claims is being told the record is complete when it is not.
// ---------------------------------------------------------------------------------------------

export function rollupKeyFor(claim, dimension) {
  const c = claim || {};
  const sk = String(c.scopeKey || '');
  if (dimension === 'individual') return String(c.createdBy || '') || null;
  if (dimension === 'committee') return String(c.committee || '') || null;
  if (dimension === 'local') {
    const m = /^LC(\d+)-/.exec(sk);
    return m ? m[1] : null;
  }
  if (dimension === 'gca') {
    // SS XI. This was a hand-written fourth copy of routing.js's rule, and it carried the same A12
    // defect: GO-SCL, GO-GTW and GO-DMIR fell out of every roll-up silently. ONE definition now -
    // resolveGeneralCommittee returns '' where it cannot parse, and a roll-up key of '' is not a
    // bucket, so it stays null here exactly as before.
    return resolveGeneralCommittee(sk) || null;
  }
  return null;
}

export function rollup(claims, dimension) {
  const list = Array.isArray(claims) ? claims : [];
  const buckets = new Map();

  list.forEach(c => {
    const k = rollupKeyFor(c, dimension);
    // A claim whose dimension cannot be resolved is NOT silently dropped - it lands in its own
    // bucket so the total always reconciles to the number of claims that went in.
    const key = k === null ? '(unresolved)' : k;
    if (!buckets.has(key)) {
      buckets.set(key, {
        key, total: 0, recorded: 0, unrecorded: 0,
        wins: 0, adverse: 0, toBoard: 0,
        amountRecovered: 0, amountRecordedOn: 0, amountUnknownOn: 0,
        amountUnsourced: 0, amountUnsourcedOn: 0,   // ruling 161: kept apart, never added to amountRecovered
        byOutcome: OUTCOME_KEYS.reduce((a, o) => { a[o] = 0; return a; }, {}),
      });
    }
    const b = buckets.get(key);
    b.total++;
    if (!hasOutcome(c)) { b.unrecorded++; return; }
    b.recorded++;
    const meta = outcomeMeta(c.outcome);
    b.byOutcome[meta.key]++;
    if (meta.win === true) b.wins++;
    else if (meta.adverse) b.adverse++;
    if (meta.key === 'progressed_to_board') b.toBoard++;
    // A null amount is UNKNOWN and is counted as unknown, never as zero dollars.
    // RULING 161: a figure with a document or pay record behind it is RECOVERED; a figure with only a
    // statement, or no basis, is UNSOURCED and summed on its own line. The two are never added. Nothing
    // here reads claim-value placeholders (worth, claimValue, PLACEHOLDER_*): a claim's estimate of what
    // it might be worth is not money it recovered.
    if (typeof c.amountRecovered === 'number' && isFinite(c.amountRecovered)) {
      if (isSourcedAmount(c)) { b.amountRecovered += c.amountRecovered; b.amountRecordedOn++; }
      else { b.amountUnsourced += c.amountRecovered; b.amountUnsourcedOn++; }
    } else {
      b.amountUnknownOn++;
    }
  });

  return [...buckets.values()].sort((a, b) => (b.total - a.total) || String(a.key).localeCompare(String(b.key)));
}

// The one-line honesty statement that belongs beside any roll-up. A percentage over a partial
// record is the single easiest way to mislead an officer about his own committee, so the coverage
// is stated first and the rate is refused outright until something is recorded.
export function rollupCaveat(bucket) {
  const b = bucket || { total: 0, recorded: 0, unrecorded: 0, amountUnknownOn: 0 };
  if (!b.total) return 'No claims in this group.';
  if (!b.recorded) {
    return b.total + ' claim(s), NONE with a recorded outcome. Nothing here is a result - the field '
         + 'did not exist before 2026-08-29 and nothing is inferred backwards (68.2).';
  }
  const parts = [b.recorded + ' of ' + b.total + ' claim(s) have a recorded outcome'];
  if (b.unrecorded) {
    parts.push(b.unrecorded + ' unrecorded - counted in the total and NOT read as a denial');
  }
  if (b.amountUnknownOn) {
    // Stated over the RECORDED outcomes, because that is the population the money question is
    // about. An open claim has not recovered nothing; it has not finished. Padding the denominator
    // with claims that never ended would understate the coverage of the one figure an officer
    // reads as money.
    parts.push(b.amountUnknownOn + ' of the ' + b.recorded + ' recorded carry no recovered figure, '
             + 'which is not the same as zero');
  }
  if (b.amountUnsourcedOn) {
    parts.push(b.amountUnsourcedOn + ' figure(s) totalling $' + Number(b.amountUnsourced || 0).toFixed(2)
             + ' rest on a statement or no basis and are NOT in the recovered total');
  }
  return parts.join('; ') + '.' + (b.amountUnsourcedOn ? ' [R-161]' : '');   // Record 597 (ruling 168): end-of-line tag
}

// Sanity: the closing stage must actually be on the ladder. If a later batch renames it, this
// throws the mismatch into the open instead of silently never matching.
export const CLOSING_STAGE_ON_LADDER = LADDER.some(s => s.key === CLOSING_STAGE);

// ---- A3.4.3: the settlement notification duty (2026-09-14, task list section 249) ---------------------
// SMART Constitution SS85 lines 53-57, as the office-succession design read it: a General Committee or
// subcommittee settling a matter must notify the interested Local Chairpersons and the Secretary of
// the originating Locals within THIRTY days. "A claim-lifecycle obligation with a clock, and the
// platform is the natural place to both prompt it and evidence it." So: for a claim whose outcome is
// SETTLED, the duty and its due date are derived from the outcome's recording date; a
// `settlementNoticeOn` on the claim (the date the notice went to the Locals) discharges it; past the
// due date with no notice it reads OVERDUE. Nothing here sends anything - it says what is owed.
export const SETTLEMENT_NOTICE_DAYS = 30;
const dayOfTs = (v) => {
  if (!v) return null;
  if (typeof v === 'string') return v.slice(0, 10);
  if (typeof v.toDate === 'function') return v.toDate().toISOString().slice(0, 10);
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  return null;
};
export function settlementNotice(claim, today) {
  const c = claim || {};
  if (c.outcome !== 'settled') return null;
  const from = dayOfTs(c.outcomeRecordedAt) || dayOfTs(c.updatedAt);
  const t = (typeof today === 'string' && today.length >= 10) ? today.slice(0, 10) : new Date().toISOString().slice(0, 10);
  if (!from) return { state: 'undated', dueBy: null, line: 'Settled - the notice to the Locals is owed within 30 days (SS85 lines 53-57), but the settlement carries no recording date to count from.' };
  // Forensic audit 2026-10-16: ruling 314 - the recording date is day one, so the 30th day is the last (timeLimit.lastDayOf);
  // from + 30 days put the due date one day late. POLICY FLAG for the owner: SS85 runs this from the settlement, and the
  // platform counts from the day the settlement was RECORDED (outcomeRecordedAt), which is never earlier than the settlement.
  const dueBy = lastDayOf(from, SETTLEMENT_NOTICE_DAYS);
  const on = dayOfTs(c.settlementNoticeOn);
  if (on) return { state: 'given', dueBy, on, line: 'Notice of the settlement given to the interested Local Chairpersons and the Secretaries of the originating Locals on ' + on + (on > dueBy ? ' (LATE - due ' + dueBy + ')' : ' (due ' + dueBy + ')') + ' - SS85 lines 53-57.' };
  if (t > dueBy) return { state: 'overdue', dueBy, line: 'NOTICE OVERDUE: SS85 lines 53-57 require notice of a settlement to the interested Local Chairpersons and the Secretaries of the originating Locals within 30 days - due ' + dueBy + ', none recorded.' };
  return { state: 'due', dueBy, line: 'Settled ' + from + ' - notice to the interested Local Chairpersons and the Secretaries of the originating Locals is due by ' + dueBy + ' (SS85 lines 53-57). Record the date it is given.' };
}
